import { registerSignOutCleanup } from "../auth/signOutCleanup";
import { useUserStore } from "../hooks/useMyUserId";

/**
 * How long a piece of view state (table filters, sorting, …) outlives the
 * component that shows it:
 * - `"none"`: only while the component is mounted.
 * - `"memory"`: across SPA navigation, until the page is reloaded.
 * - `"browser"`: in localStorage, across reloads and browser restarts.
 *
 * Stored view state is scoped to the signed-in user and removed when they sign out.
 */
export type AtomViewStatePersistence = "none" | "memory" | "browser";

const KEY_PREFIX = "atom-view-state";
const FORMAT_VERSION = 1;
const DATE_TAG = "$date";

function userSegment(userId: string | null): string {
  return userId ?? "anonymous";
}

/** Storage key of a view state entry for the currently signed-in user. */
export function getViewStateStorageKey(kind: string, key: string): string {
  const { userId } = useUserStore.getState();
  return `${KEY_PREFIX}:${userSegment(userId)}:${kind}:${key}`;
}

/**
 * Storage backing a persistence level, or undefined if the level is not
 * stored at all. `sessionStorage` would slot in here as a further level.
 */
export function getViewStateStorage(
  persistence: AtomViewStatePersistence,
): Storage | undefined {
  if (persistence !== "browser") return undefined;
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    // Access can throw, e.g. when storage is disabled for the site.
    return undefined;
  }
}

function isTaggedDate(value: unknown): value is { [DATE_TAG]: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.keys(value).length === 1 &&
    typeof (value as Record<string, unknown>)[DATE_TAG] === "string"
  );
}

function serialize(value: unknown): string {
  // `this[key]` is the raw value; `current` has already been through toJSON,
  // which would turn a Date into a plain string.
  return JSON.stringify(
    { v: FORMAT_VERSION, value },
    function (this: Record<string, unknown>, key: string, current: unknown) {
      const raw = this[key];
      return raw instanceof Date ? { [DATE_TAG]: raw.toISOString() } : current;
    },
  );
}

function deserialize(text: string): unknown {
  const parsed: unknown = JSON.parse(text, (_key, current: unknown) => {
    if (!isTaggedDate(current)) return current;
    const date = new Date(current[DATE_TAG]);
    return Number.isNaN(date.getTime()) ? undefined : date;
  });
  if (
    typeof parsed !== "object" ||
    parsed === null ||
    (parsed as { v?: unknown }).v !== FORMAT_VERSION
  ) {
    return undefined;
  }
  return (parsed as { value?: unknown }).value;
}

/** Reads a stored entry; unreadable, corrupt or outdated entries yield undefined. */
export function readStoredViewState(
  storage: Storage,
  storageKey: string,
): unknown {
  try {
    const text = storage.getItem(storageKey);
    return text === null ? undefined : deserialize(text);
  } catch {
    return undefined;
  }
}

export function writeStoredViewState(
  storage: Storage,
  storageKey: string,
  value: unknown,
): void {
  try {
    storage.setItem(storageKey, serialize(value));
  } catch {
    // Quota exceeded or storage unavailable: the value stays in memory only.
  }
}

/**
 * Moves an entry from an older, unscoped storage format to `storageKey`.
 * The legacy entry is removed either way, so it is migrated at most once.
 */
export function migrateLegacyViewState(
  storage: Storage,
  legacyStorageKey: string,
  storageKey: string,
): unknown {
  try {
    const text = storage.getItem(legacyStorageKey);
    if (text === null) return undefined;
    storage.removeItem(legacyStorageKey);
    const value: unknown = JSON.parse(text);
    writeStoredViewState(storage, storageKey, value);
    return value;
  } catch {
    return undefined;
  }
}

/** Removes all stored view state of a user. */
export function clearStoredViewState(userId: string | null): void {
  const storage = getViewStateStorage("browser");
  if (!storage) return;
  const prefix = `${KEY_PREFIX}:${userSegment(userId)}:`;
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    // Storage unavailable: nothing was stored either.
  }
}

registerSignOutCleanup(clearStoredViewState);
